/**
 * What each prop *is*, and which props belong on which level.
 *
 * Split out of `props.ts` because the placement policy and the catalogue change
 * for different reasons: the policy is about where a player walks, this is about
 * what a chair is. Nothing here knows about the grid.
 *
 * A `PropDef` answers the three questions the placer asks of every candidate
 * spot: does this thing need a wall behind it, is it wide enough to plug a
 * one-cell corridor, and how high off the floor does it sit.
 *
 * One contract with the modelling side, because `PropFootprint` has no offset
 * field: **a kind that carries a footprint must be authored centred on its own
 * origin in XZ.** The placer sets a wall-backed solid prop back by its own
 * `halfZ`, so a model authored with its back face at the origin — the older
 * convention, still used by `cabinet` and `vending` — would stand half a body
 * inside the wall. Kinds without a footprint are unaffected and keep the old
 * convention.
 *
 * Every footprint here has been measured against the model that renders it —
 * the widest of its baked variants, on every level, with the view's own yaw
 * jitter and lean applied. A box narrower than its model is an object the
 * player walks through; a box wider than it is an invisible wall. When a model
 * changes, re-measure: nothing in the type system ties the two together.
 */

import type { GeneratorKind, PropFootprint, PropKind } from '../../core/types';

/**
 * How a prop attaches to the world. This decides the position offset, the yaw
 * and the `height` the placer writes into the `PropSpot`.
 */
export type PropMount =
  /** Stands on the floor with its back against a wall. */
  | 'floor'
  /** Lies flat on the floor. Reads as debris; never in the way. */
  | 'floor-flat'
  /** Fixed to a wall surface at `height` metres. Decals and fittings. */
  | 'wall'
  /** Hangs from or stains the ceiling. Placed at `spec.ceilingHeight`. */
  | 'ceiling'
  /**
   * Stands out in the open, away from any wall — a bathtub in the middle of a
   * room, a doorframe leading nowhere. This is the mount that makes a space
   * feel wrong rather than merely used, and it is only safe because these
   * carry a `footprint` and the player collides with them. The placer draws
   * these from `PropSites.frees` rather than from the wall-hugging host pool.
   */
  | 'floor-free';

export interface PropDef {
  kind: PropKind;
  mount: PropMount;
  /** Must have a solid orthogonal neighbour to sit against. */
  wall: boolean;
  /**
   * Wide enough that it would plug a one-cell corridor or a threshold. Solid
   * props are refused those spots outright by the footprint test; for the ones
   * the player still walks through, this is what stops a cabinet standing in a
   * doorway and lying about where the space goes.
   */
  bulky: boolean;
  /** Reads best wedged into an inside corner, and is worth steering there. */
  corner: boolean;
  /** Mount height range in metres. Only meaningful for `wall` mounts. */
  height: readonly [number, number];
  /** Uniform scale range. Modest: a chair should never read as a different object. */
  scale: readonly [number, number];
  /**
   * Contiguous repeats along the same wall. A single length of pipe is a prop;
   * four of them in a row is a service corridor.
   */
  run: readonly [number, number];
  /**
   * Collision box at scale 1, copied onto every `PropSpot` of this kind.
   * Omitted for anything the player should walk through: decals, debris,
   * ceiling fittings, and the one free-standing kind that is mostly hole.
   */
  footprint?: PropFootprint;
}

function def(kind: PropKind, mount: PropMount, o: Partial<PropDef> = {}): PropDef {
  // A footprint has no base elevation — it always starts at the floor — so
  // hanging one off the ceiling or up a wall would block the floor underneath
  // it, where there is plainly nothing. Caught here rather than left as a
  // comment, because the symptom is a player stopped by thin air.
  if (o.footprint && (mount === 'ceiling' || mount === 'wall')) {
    throw new Error(`prop '${kind}': a ${mount} mount cannot carry a footprint`);
  }
  return {
    kind,
    mount,
    wall: o.wall ?? (mount === 'floor' || mount === 'wall'),
    bulky: o.bulky ?? false,
    corner: o.corner ?? false,
    height: o.height ?? [0, 0],
    scale: o.scale ?? [0.9, 1.1],
    run: o.run ?? [1, 1],
    ...(o.footprint ? { footprint: o.footprint } : {}),
  };
}

/** Shorthand for a collision box, since most of them are symmetric. */
function fp(halfX: number, halfZ: number, height: number): PropFootprint {
  return { halfX, halfZ, height };
}

/**
 * The catalogue. Heights are chosen so a wall prop reads at a standing player's
 * eye line (1.68 m) or deliberately below it; `props.ts` clamps anything that
 * would clip a low ceiling.
 */
export const PROP_DEFS: Record<PropKind, PropDef> = {
  // -- Lobby ------------------------------------------------------------
  chair: def('chair', 'floor', { corner: true, scale: [0.88, 1.12] }),
  boxes: def('boxes', 'floor', { corner: true, run: [1, 3], scale: [0.85, 1.15] }),
  cabinet: def('cabinet', 'floor', { bulky: true, run: [1, 2], scale: [0.94, 1.08] }),
  bucket: def('bucket', 'floor', { corner: true, scale: [0.85, 1.08] }),
  papers: def('papers', 'floor-flat', { scale: [0.8, 1.2] }),
  vending: def('vending', 'floor', { bulky: true, scale: [0.96, 1.04] }),
  payphone: def('payphone', 'wall', { height: [1.05, 1.3], scale: [0.95, 1.05] }),
  'hanging-light': def('hanging-light', 'ceiling', { scale: [0.9, 1.12] }),
  'ceiling-stain': def('ceiling-stain', 'ceiling', { scale: [0.8, 1.5] }),

  // -- Warehouse --------------------------------------------------------
  pallet: def('pallet', 'floor-flat', { run: [1, 3], scale: [0.92, 1.08] }),
  crate: def('crate', 'floor', { bulky: true, corner: true, run: [1, 3], scale: [0.85, 1.15] }),
  shelving: def('shelving', 'floor', { bulky: true, run: [2, 4], scale: [0.95, 1.06] }),
  cone: def('cone', 'floor', { scale: [0.88, 1.12] }),
  drum: def('drum', 'floor', { corner: true, run: [1, 3], scale: [0.9, 1.1] }),
  handtruck: def('handtruck', 'floor', { scale: [0.92, 1.08] }),

  // -- Pipes ------------------------------------------------------------
  'pipe-run': def('pipe-run', 'wall', { height: [1.85, 2.25], run: [2, 5], scale: [0.9, 1.12] }),
  valve: def('valve', 'wall', { height: [1.0, 1.6], scale: [0.85, 1.15] }),
  'junction-box': def('junction-box', 'wall', { height: [1.2, 1.75], scale: [0.9, 1.1] }),
  // Anchored at the floor even though it hangs off a wall: a ladder you cannot
  // reach the bottom rung of is a prop nobody believes.
  ladder: def('ladder', 'wall', { height: [0, 0], scale: [0.95, 1.05] }),
  puddle: def('puddle', 'floor-flat', { wall: false, scale: [0.85, 1.4] }),

  // -- Poolrooms --------------------------------------------------------
  // Mounted, but to the coping at the edge of a pool rather than to a grid
  // wall — so `wall` is false and the water dressing pass places it directly.
  'pool-ladder': def('pool-ladder', 'wall', { wall: false, height: [0, 0], scale: [0.95, 1.05] }),
  bench: def('bench', 'floor', { run: [1, 2], scale: [0.92, 1.08] }),
  'float-ring': def('float-ring', 'floor-flat', { wall: false, scale: [0.9, 1.12] }),
  drain: def('drain', 'floor-flat', { wall: false, scale: [0.9, 1.1] }),
  towel: def('towel', 'floor-flat', { scale: [0.85, 1.15] }),

  // -- Landmarks --------------------------------------------------------
  graffiti: def('graffiti', 'wall', { height: [1.1, 1.75], scale: [0.8, 1.3] }),
  'tally-marks': def('tally-marks', 'wall', { height: [0.95, 1.4], scale: [0.85, 1.12] }),
  arrow: def('arrow', 'wall', { height: [1.05, 1.55], scale: [0.9, 1.18] }),
  sign: def('sign', 'wall', { height: [1.95, 2.35], scale: [0.9, 1.08] }),
  camp: def('camp', 'floor', { corner: true, scale: [0.95, 1.06] }),

  // -- Architecture that does not make sense ----------------------------
  // These are the reason a room stops reading as a rectangle. All solid: a
  // wall you can walk through is worse than no wall at all.
  'half-wall': def('half-wall', 'floor-free', { wall: false, scale: [0.9, 1.15], footprint: fp(1.53, 0.2, 1.05) }),
  'stub-wall': def('stub-wall', 'floor-free', { wall: false, scale: [0.9, 1.2], footprint: fp(1.2, 0.19, 3.0) }),
  // No footprint, deliberately. A doorframe is mostly hole, and `PropFootprint`
  // is one box: a box across the opening would be an invisible wall inside a
  // visible doorway, which is a worse lie than clipping a 10 cm jamb. It is
  // still registered as occupying its cell so nothing else lands on top of it.
  'free-doorframe': def('free-doorframe', 'floor-free', { wall: false, scale: [0.95, 1.05] }),
  'false-door': def('false-door', 'wall', { height: [0, 0], scale: [0.96, 1.04] }),
  soffit: def('soffit', 'ceiling', { wall: false, scale: [0.9, 1.3] }),
  'column-cluster': def('column-cluster', 'floor-free', { wall: false, scale: [0.9, 1.1], footprint: fp(0.6, 0.53, 3.0) }),
  'stairs-nowhere': def('stairs-nowhere', 'floor', { bulky: true, scale: [0.95, 1.08], footprint: fp(0.72, 0.57, 0.9) }),
  partition: def('partition', 'floor-free', { wall: false, run: [1, 3], scale: [0.92, 1.1], footprint: fp(0.88, 0.12, 1.55) }),
  'inner-window': def('inner-window', 'wall', { height: [1.1, 1.45], scale: [0.92, 1.12] }),

  // -- Objects that should not be here ----------------------------------
  bathtub: def('bathtub', 'floor-free', { wall: false, scale: [0.96, 1.04], footprint: fp(0.85, 0.45, 0.62) }),
  'tv-stack': def('tv-stack', 'floor-free', { wall: false, scale: [0.9, 1.12], footprint: fp(0.38, 0.34, 1.3) }),
  'phone-booth': def('phone-booth', 'floor-free', { wall: false, scale: [0.96, 1.04], footprint: fp(0.5, 0.5, 2.3) }),
  'shopping-cart': def('shopping-cart', 'floor-free', { wall: false, scale: [0.94, 1.06], footprint: fp(0.44, 0.6, 1.0) }),
  // Every variant of the model is a mattress *leaning* against the wall, 1.5 to
  // 1.9 m of it, so the box has to stop the player: at the old 0.34 it was under
  // `stepOverHeight` and the player walked through a slab taller than they are.
  // A mattress lying flat would be the one to make steppable; there isn't one.
  mattress: def('mattress', 'floor', { scale: [0.94, 1.08], footprint: fp(0.71, 0.35, 1.5) }),
  piano: def('piano', 'floor-free', { wall: false, bulky: true, scale: [0.97, 1.03], footprint: fp(0.8, 0.43, 1.25) }),
  'fish-tank': def('fish-tank', 'floor-free', { wall: false, scale: [0.9, 1.1], footprint: fp(0.52, 0.29, 1.2) }),
  chandelier: def('chandelier', 'ceiling', { wall: false, scale: [0.85, 1.15] }),
  mannequin: def('mannequin', 'floor-free', { wall: false, corner: true, scale: [0.95, 1.05], footprint: fp(0.26, 0.22, 1.8) }),
  'chair-pile': def('chair-pile', 'floor-free', { wall: false, bulky: true, scale: [0.9, 1.15], footprint: fp(0.6, 0.43, 1.5) }),
  'door-pile': def('door-pile', 'floor-free', { wall: false, bulky: true, scale: [0.92, 1.1], footprint: fp(0.48, 0.49, 1.1) }),
  desk: def('desk', 'floor-free', { wall: false, scale: [0.94, 1.06], footprint: fp(0.76, 0.43, 0.76) }),
  bookshelf: def('bookshelf', 'floor', { bulky: true, scale: [0.94, 1.08], footprint: fp(0.46, 0.19, 1.9) }),
  mirror: def('mirror', 'floor', { scale: [0.92, 1.1], footprint: fp(0.35, 0.19, 1.7) }),
  'dead-plant': def('dead-plant', 'floor-free', { wall: false, scale: [0.85, 1.15], footprint: fp(0.39, 0.38, 0.87) }),
  'bed-frame': def('bed-frame', 'floor-free', { wall: false, bulky: true, scale: [0.96, 1.04], footprint: fp(0.55, 1.0, 0.62) }),
  sofa: def('sofa', 'floor-free', { wall: false, bulky: true, scale: [0.95, 1.06], footprint: fp(0.95, 0.47, 0.82) }),
  'standing-lamp': def('standing-lamp', 'floor-free', { wall: false, scale: [0.9, 1.1], footprint: fp(0.29, 0.29, 1.6) }),
  'wall-clock': def('wall-clock', 'wall', { height: [2.0, 2.4], scale: [0.9, 1.1] }),
  // Plumbed into nothing, standing in the middle of a carpeted office. The
  // single clearest statement this catalogue makes, so it does not go on a wall.
  sink: def('sink', 'floor-free', { wall: false, scale: [0.94, 1.06], footprint: fp(0.32, 0.25, 0.9) }),
  'filing-tower': def('filing-tower', 'floor-free', { wall: false, bulky: true, scale: [0.94, 1.1], footprint: fp(0.3, 0.39, 2.4) }),
  // 0.7 is the stacked-pair variant, not the single roll: one box per kind has
  // to cover the tallest thing the kind can be, or a third of them are 70 cm of
  // carpet the player strolls through.
  'carpet-roll': def('carpet-roll', 'floor', { run: [1, 2], scale: [0.9, 1.15], footprint: fp(0.91, 0.28, 0.7) }),
  hydrant: def('hydrant', 'floor-free', { wall: false, scale: [0.92, 1.08], footprint: fp(0.27, 0.27, 0.85) }),
  // No footprint: it is nine centimetres tall and the player steps over it
  // without noticing, which is the correct outcome for something they are meant
  // to walk past and then stop.
  shoe: def('shoe', 'floor-flat', { scale: [0.94, 1.06] }),
  bicycle: def('bicycle', 'floor', { scale: [0.94, 1.06], footprint: fp(0.8, 0.52, 0.95) }),

  // -- The way down ------------------------------------------------------
  // Placed by the exit pass rather than by the cluster pass, so it never has to
  // compete for a spot. Backed against a wall because a slide has to come out
  // of something.
  slide: def('slide', 'floor', { bulky: true, scale: [1, 1], footprint: fp(0.95, 1.25, 2.2) }),

  // -- The Funrooms ------------------------------------------------------
  // A soft-play centre is not a hall with equipment in it: the equipment is the
  // hall. Two of these kinds are therefore not props in the ordinary sense any
  // more, they are *fit-out modules* that `world/gen/funrooms.ts` tiles across a
  // whole bay, and both of them are sized to the level's 3.4 m cell so that
  // neighbouring copies join up rather than merely stand near each other.
  //
  // A module that tiles cannot be scaled. `scale: [1, 1]` on both is load-
  // bearing in exactly the way the three house kinds' is: a deck 8% larger than
  // its neighbour is a step in the middle of a first floor, and a ball field 8%
  // larger is a seam of bare carpet down the middle of a pool.

  /*
   * One bay of the climbing frame: a padded column, and a deck at 2.25 m with
   * whatever that module carries above it — netting, an upper deck, a tube run,
   * a canopy.
   *
   * The box is the *column* and nothing else, and that is the whole reason this
   * level can be filled at all. Everything the module carries lives at or above
   * 2.13 m, which is head height by the same reckoning the tubes already use, so
   * it costs nothing in collision: the player walks under the frame between
   * posts set 3.4 m apart, with 2.8 m of clear floor between any two of them.
   * Fill the volume, leave the plan passable.
   *
   * A 0.34 m box against a 3.4 m cell is also what makes the lattice provably
   * safe. The blocked disc — box plus the player's radius — is 0.66 m across a
   * 3.4 m pitch, so the discs are disjoint, each one sits a metre clear of its
   * own cell boundary, and a set of disjoint interior discs cannot disconnect
   * the floor they stand on.
   */
  'play-structure': def('play-structure', 'floor-free', { wall: false, scale: [1, 1], footprint: fp(0.34, 0.34, 2.6) }),
  // Four metres of chute coming off a 2.25 m platform, which is what getting
  // down from the height of a frame deck actually takes. It was a 1.2 m nursery
  // slide standing on its own; it is now the way off the first floor, and the
  // box grew with it. One of the longest props in the game, and refused
  // wherever there is not room for one.
  'play-slide': def('play-slide', 'floor-free', { wall: false, bulky: true, scale: [0.95, 1.05], footprint: fp(0.55, 2.15, 2.9) }),

  /*
   * A ball field, and *no footprint at all*.
   *
   * It used to be a 3.5 m walled pit with a half-metre box round it, which is a
   * wall with balls on it — the player walked round the one thing on the level
   * they would obviously want to walk into. A ball pool you cannot enter is the
   * opposite of the request, so the wall is gone and so is the box: the balls
   * are a field the player wades through, four cells of them per tile, thinning
   * to loose balls that have rolled out across the carpet.
   *
   * Same reasoning as `free-doorframe`, which also carries none: `PropFootprint`
   * is one box, and there is nothing here for a box to describe. It is still
   * registered as occupying its cell, so nothing else lands in the middle of it.
   */
  'ball-pit': def('ball-pit', 'floor-free', { wall: false, scale: [1, 1] }),
  'crawl-tube': def('crawl-tube', 'floor-free', { wall: false, run: [1, 3], scale: [0.95, 1.1], footprint: fp(0.45, 0.95, 1.15) }),
  'soft-blocks': def('soft-blocks', 'floor', { corner: true, run: [1, 3], scale: [0.85, 1.2], footprint: fp(0.42, 0.42, 0.55) }),
  balloons: def('balloons', 'floor', { corner: true, scale: [0.85, 1.15], footprint: fp(0.22, 0.22, 1.5) }),
  streamers: def('streamers', 'ceiling', { wall: false, scale: [0.85, 1.3] }),
  // The atlas behind this kind carries the venue's wall *lining* as well as its
  // banners — quilted crash padding at dado height, and the net panels above it
  // — because a soft-play hall has no bare wall in it anywhere and neither of
  // those survives being modelled out of boxes any better than a banner does.
  // The mount range therefore runs from a dado to a high-level swag; the
  // dressing pass in `funrooms.ts` sets the height outright for the ones it
  // hangs, and this range is what the general placer draws for the rest.
  banner: def('banner', 'wall', { height: [1.05, 2.45], scale: [0.95, 1.2] }),
  'party-table': def('party-table', 'floor-free', { wall: false, bulky: true, run: [1, 2], scale: [0.95, 1.08], footprint: fp(0.85, 0.4, 0.78) }),
  'arcade-cabinet': def('arcade-cabinet', 'floor', { bulky: true, run: [1, 3], scale: [0.96, 1.05], footprint: fp(0.34, 0.36, 1.75) }),
  // Measured off the model rather than guessed: the slumped castle reaches 1.50
  // by 1.41, and a 1.8 box put 30-40 cm of invisible wall around a thing whose
  // whole point is that it is flat on the floor.
  'bouncy-castle': def('bouncy-castle', 'floor-free', { wall: false, bulky: true, scale: [0.95, 1.15], footprint: fp(1.56, 1.48, 0.9) }),
  confetti: def('confetti', 'floor-flat', { wall: false, scale: [0.85, 1.4] }),

  // -- Level 94 ----------------------------------------------------------
  // Outdoors, so almost none of it wants a wall behind it. The fence and the
  // hedge are what turn open lawn into somebody's plot.
  // A cell long, like `pipe-run`: one panel per cell, and a boundary made of
  // panels that stop short of the cell edge is a fence with a person-wide gap
  // every three metres.
  'picket-fence': def('picket-fence', 'floor-free', { wall: false, run: [2, 6], scale: [0.95, 1.05], footprint: fp(1.63, 0.09, 1.0) }),
  mailbox: def('mailbox', 'floor-free', { wall: false, scale: [0.92, 1.08], footprint: fp(0.18, 0.18, 1.15) }),
  'lamp-post': def('lamp-post', 'floor-free', { wall: false, scale: [0.95, 1.08], footprint: fp(0.16, 0.16, 3.4) }),
  shrub: def('shrub', 'floor-free', { wall: false, run: [1, 4], scale: [0.8, 1.25], footprint: fp(0.45, 0.45, 0.95) }),
  'garden-tree': def('garden-tree', 'floor-free', { wall: false, bulky: true, scale: [0.85, 1.25], footprint: fp(0.32, 0.32, 4.2) }),
  'swing-set': def('swing-set', 'floor-free', { wall: false, bulky: true, scale: [0.95, 1.08], footprint: fp(1.35, 0.75, 2.3) }),
  'parked-car': def('parked-car', 'floor-free', { wall: false, bulky: true, scale: [0.97, 1.03], footprint: fp(0.85, 2.05, 1.5) }),
  'water-tower': def('water-tower', 'floor-free', { wall: false, bulky: true, scale: [1, 1], footprint: fp(2.2, 2.2, 12) }),
  'laundry-line': def('laundry-line', 'floor-free', { wall: false, scale: [0.9, 1.15], footprint: fp(1.6, 0.08, 2.0) }),

  /*
   * The three house kinds. No footprint, and never scaled.
   *
   * `PropSpot.scale` is uniform, and every one of these is baked per building
   * size in cells — a roof for a five-by-four house is a different mesh, not a
   * stretched one — so a scale range here would be a size error on every house
   * on the level. `scale: [1, 1]` is load-bearing.
   *
   * No footprint because none of them stands on the ground the player walks on:
   * a roof is seven metres up, a mound is a bank against a wall, and a window
   * bay is 16 cm proud of a wall that is already solid. One footprint box on any
   * of them would seal the building it belongs to. `world/gen/motion.ts` places
   * them itself, fitted to the plan, and adds a five-centimetre mark to roofs
   * by hand purely to keep them out of the scenery cull.
   *
   * They are deliberately absent from `THEMES`, `CAMP_KIT`, `oddityKinds` and
   * `hoardKinds`: the general placer has no idea where a building is.
   */
  'house-hill': def('house-hill', 'floor-free', { wall: false, scale: [1, 1] }),
  'house-roof': def('house-roof', 'floor-free', { wall: false, scale: [1, 1] }),
  'house-trim': def('house-trim', 'floor-free', { wall: false, scale: [1, 1] }),
};

/**
 * A cluster of props is drawn from one theme so it reads as somebody's leftover
 * rather than as a random draw. The lead always goes at the cluster's own spot;
 * the extras fill nearby slots if there are any.
 */
export interface PropTheme {
  lead: PropKind;
  extras: readonly PropKind[];
  weight: number;
}

/**
 * Per-level theme tables. Landmarks are deliberately absent — they are placed
 * structurally.
 *
 * The five levels added below the original seven mostly want an existing
 * catalogue with the weights moved, so they are built from these rather than
 * written out again: an office floor is the Lobby's furniture with more of it,
 * a plant room is the pipe level's, a hotel corridor is the Lobby's with the
 * domestic end brought forward. Only the city needs a table of its own, and
 * Lights Out needs none at all.
 */
const BASE_THEMES = {
  lobby: [
    { lead: 'boxes', extras: ['boxes', 'bucket', 'papers'], weight: 26 },
    { lead: 'chair', extras: ['chair', 'papers', 'bucket'], weight: 24 },
    { lead: 'cabinet', extras: ['papers', 'chair', 'boxes'], weight: 15 },
    { lead: 'bucket', extras: ['bucket', 'papers'], weight: 14 },
    { lead: 'papers', extras: ['papers', 'bucket'], weight: 11 },
    { lead: 'vending', extras: ['papers', 'bucket'], weight: 5 },
    { lead: 'payphone', extras: ['papers'], weight: 5 },
  ],
  warehouse: [
    { lead: 'pallet', extras: ['pallet', 'crate', 'cone'], weight: 26 },
    { lead: 'crate', extras: ['crate', 'pallet', 'drum'], weight: 24 },
    { lead: 'drum', extras: ['drum', 'pallet', 'cone'], weight: 18 },
    { lead: 'shelving', extras: ['crate', 'pallet'], weight: 14 },
    { lead: 'cone', extras: ['cone', 'pallet'], weight: 10 },
    { lead: 'handtruck', extras: ['crate', 'pallet'], weight: 8 },
  ],
  pipes: [
    { lead: 'pipe-run', extras: ['valve', 'junction-box', 'pipe-run'], weight: 34 },
    { lead: 'junction-box', extras: ['valve', 'pipe-run'], weight: 21 },
    { lead: 'valve', extras: ['pipe-run', 'junction-box'], weight: 18 },
    { lead: 'puddle', extras: ['puddle'], weight: 15 },
    { lead: 'ladder', extras: ['junction-box'], weight: 12 },
  ],
  poolrooms: [
    { lead: 'bench', extras: ['towel', 'bench'], weight: 34 },
    { lead: 'towel', extras: ['towel', 'bench'], weight: 28 },
    { lead: 'drain', extras: ['towel'], weight: 24 },
    { lead: 'bench', extras: ['towel'], weight: 14 },
  ],
  // The Alcove is the one place someone plainly stopped and tidied up.
  hub: [
    { lead: 'chair', extras: ['chair', 'chair', 'boxes'], weight: 40 },
    { lead: 'boxes', extras: ['boxes', 'papers'], weight: 24 },
    { lead: 'cabinet', extras: ['papers', 'chair'], weight: 16 },
    { lead: 'bucket', extras: ['papers'], weight: 12 },
    { lead: 'payphone', extras: ['papers'], weight: 8 },
  ],
  // Everything here was set out for the party and then left. Seating in rows,
  // machines against the wall, and the paper goods still boxed — the clutter of
  // a venue that was made ready rather than of one that was used.
  //
  // `play-structure` is deliberately absent, and so is it from `oddityKinds`.
  // It stopped being an object and became a module of a climbing frame — a
  // padded column carrying a deck that only makes sense with the next one along
  // — so a lone one dropped in a corridor by this pass would be a platform on a
  // post. `world/gen/funrooms.ts` is the only thing that places them now, and it
  // places them in blocks.
  funrooms: [
    { lead: 'ball-pit', extras: ['soft-blocks', 'balloons', 'confetti'], weight: 22 },
    { lead: 'crawl-tube', extras: ['crawl-tube', 'soft-blocks', 'balloons'], weight: 20 },
    { lead: 'party-table', extras: ['balloons', 'confetti', 'banner'], weight: 18 },
    { lead: 'play-slide', extras: ['soft-blocks', 'confetti'], weight: 14 },
    { lead: 'arcade-cabinet', extras: ['arcade-cabinet', 'banner'], weight: 12 },
    { lead: 'bouncy-castle', extras: ['balloons', 'confetti'], weight: 8 },
    // The banner is a wall mount and every other kind here wants the middle of
    // the floor, so it only ever arrives as somebody's extra. Two themes carry
    // it, which is about one hall in three with something written on the wall.
    { lead: 'soft-blocks', extras: ['soft-blocks', 'confetti', 'banner'], weight: 6 },
  ],
  // Outdoors, at the kerb. Bins and bagged rubbish, a bike against a wall, the
  // cones and pallets of work nobody came back to finish.
  motion: [
    { lead: 'picket-fence', extras: ['shrub', 'mailbox', 'shrub'], weight: 24 },
    { lead: 'shrub', extras: ['shrub', 'garden-tree'], weight: 20 },
    { lead: 'garden-tree', extras: ['shrub', 'laundry-line'], weight: 16 },
    { lead: 'parked-car', extras: ['mailbox', 'shrub'], weight: 14 },
    { lead: 'swing-set', extras: ['shrub', 'laundry-line'], weight: 12 },
    { lead: 'lamp-post', extras: ['shrub'], weight: 8 },
    { lead: 'laundry-line', extras: ['shrub'], weight: 6 },
  ],
} satisfies Record<string, readonly PropTheme[]>;

export const THEMES: Record<GeneratorKind, readonly PropTheme[]> = {
  ...BASE_THEMES,

  // An office floor plate that was still occupied. Chairs out of the cube farm,
  // paper everywhere, and the machines against the core wall.
  offices: [
    { lead: 'chair', extras: ['chair', 'papers', 'papers'], weight: 30 },
    { lead: 'papers', extras: ['papers', 'chair'], weight: 22 },
    { lead: 'boxes', extras: ['boxes', 'papers'], weight: 18 },
    { lead: 'cabinet', extras: ['papers', 'chair', 'boxes'], weight: 14 },
    { lead: 'vending', extras: ['papers', 'bucket'], weight: 8 },
    { lead: 'payphone', extras: ['papers'], weight: 8 },
  ],

  // A plant room that is still running. The pipe catalogue, plus the drums and
  // pallets of the stores it was serviced from.
  station: [
    { lead: 'pipe-run', extras: ['valve', 'junction-box', 'pipe-run'], weight: 32 },
    { lead: 'junction-box', extras: ['valve', 'pipe-run'], weight: 22 },
    { lead: 'valve', extras: ['pipe-run', 'junction-box'], weight: 16 },
    { lead: 'drum', extras: ['drum', 'pallet', 'cone'], weight: 12 },
    { lead: 'ladder', extras: ['junction-box'], weight: 10 },
    { lead: 'puddle', extras: ['puddle'], weight: 8 },
  ],

  // "Mostly clean, with little dust." The only level in the game where the
  // clutter is a *service* — the trolley left in the corridor, the chairs from
  // a function room — rather than somebody's abandoned belongings.
  hotel: [
    { lead: 'chair', extras: ['chair', 'chair'], weight: 26 },
    { lead: 'cabinet', extras: ['papers', 'chair'], weight: 20 },
    { lead: 'boxes', extras: ['boxes', 'papers'], weight: 16 },
    { lead: 'papers', extras: ['papers'], weight: 14 },
    { lead: 'bucket', extras: ['bucket', 'papers'], weight: 12 },
    { lead: 'payphone', extras: ['papers'], weight: 12 },
  ],

  /*
   * Nothing. The wiki's Level 6 is bare concrete and its whole premise is that
   * there is nothing there — "it's dangerous because there's nothing there" —
   * so an empty table is the level, not a gap in it. `placeClusters` checks for
   * this and does no work; the landmark, architecture and oddity passes are
   * separately zeroed in `DENSITY.lightsout`.
   */
  lightsout: [],

  /*
   * A street, not a lawn. This is the one new level that could not borrow:
   * `motion`'s table leads with fences, shrubs and garden trees, all of which
   * want grass, and Level 11 is asphalt from edge to edge. Lamp posts and
   * parked cars are what it wanted out of that table and they are here without
   * the gardening; the rest is the kerbside — cones, drums, a bike against a
   * wall, the pallets of work nobody came back to finish.
   */
  city: [
    { lead: 'parked-car', extras: ['cone', 'drum'], weight: 24 },
    { lead: 'lamp-post', extras: ['cone'], weight: 20 },
    { lead: 'cone', extras: ['cone', 'drum', 'pallet'], weight: 16 },
    { lead: 'drum', extras: ['drum', 'pallet', 'cone'], weight: 14 },
    { lead: 'boxes', extras: ['boxes', 'papers'], weight: 12 },
    { lead: 'crate', extras: ['crate', 'pallet'], weight: 8 },
    { lead: 'mailbox', extras: ['papers'], weight: 6 },
  ],
};

/**
 * What is lying around a `camp`. A sleeping spot on its own is a shape on the
 * floor; a sleeping spot with somebody's rubbish beside it is a person.
 */
export const CAMP_KIT: Record<GeneratorKind, readonly PropKind[]> = {
  lobby: ['boxes', 'papers', 'bucket'],
  offices: ['boxes', 'papers', 'chair'],
  station: ['drum', 'junction-box', 'papers'],
  hotel: ['boxes', 'papers', 'chair'],
  // Whatever they were carrying, because the level supplies nothing. This is
  // the one thing on Level 6 that somebody put there.
  lightsout: ['boxes', 'papers', 'bucket'],
  city: ['crate', 'papers', 'cone'],
  warehouse: ['crate', 'pallet', 'drum'],
  pipes: ['puddle', 'junction-box'],
  poolrooms: ['towel', 'bench'],
  hub: ['boxes', 'papers', 'chair'],
  funrooms: ['soft-blocks', 'confetti', 'balloons'],
  motion: ['shrub', 'papers', 'mailbox'],
};

/**
 * Density and landmark budgets per generator.
 *
 * `cellsPerCluster` is the only number that really moves the feel: it is how
 * many walkable cells the level gets per *pocket* of scenery, not per prop. A
 * pocket averages a little over two props, so the prop count lands near
 * `walkable / cellsPerCluster * 2.3` plus the landmark and ceiling budgets.
 *
 * The Lobby's number comes out of a walked route rather than a target
 * percentage. A Level 0 room is about 18 x 21 m and takes seven or eight
 * seconds to cross, the map is 420 m on a side, and a run to an exit is four
 * hundred metres of walking. At 195 cells per pocket the player passed
 * something worth remembering roughly every half minute, with two or three
 * completely bare rooms between each.
 *
 * It now sits at 155, but the extra scenery is not more of the same junk: the
 * architecture and oddity passes carry most of the increase, and they place one
 * thing per room rather than a pocket of three. Ordinary leftovers are the
 * texture the strange objects are read against, so the ratio between them
 * matters more than either count — too many boxes and the bathtub is furniture.
 */
export interface DensityProfile {
  cellsPerCluster: number;
  /** Cluster sizes are drawn from 1..max, weighted toward the small end. */
  clusterMax: number;
  /** Cells of falloff stamped around a placed cluster, so pockets stay apart. */
  spacing: number;
  /** Ceiling props per 1000 walkable cells. */
  ceilingPer1k: number;
  /** Kinds used by the ceiling pass, in preference order. */
  ceilingKinds: readonly PropKind[];
  /** Inclusive count ranges for the landmark pass. */
  landmarks: Readonly<Record<'graffiti' | 'tally-marks' | 'arrow' | 'sign' | 'camp', readonly [number, number]>>;

  /**
   * Structural nonsense per 1000 walkable cells: the wall that stops, the
   * doorframe standing on its own. Counted per area rather than per room
   * because a warehouse bay and a Lobby closet are both "one room".
   */
  architecturePer1k: number;
  /** What that pass draws from. */
  architectureKinds: readonly PropWeight[];

  /**
   * The beat for out-of-place objects, rolled once per qualifying room.
   *
   * The variance is the point, so these are deliberately small: four rooms in
   * five hold nothing at all, which is what makes the fifth one land. A room
   * that draws `oddity` gets exactly ONE object — a bathtub alone in a bare
   * office is the whole effect, and a bathroom suite is just furniture.
   */
  oddityChance: number;
  /** Rarer still: the room is crammed with one object repeated in rows. */
  hoardChance: number;
  oddityKinds: readonly PropWeight[];
  /**
   * Kinds a hoard may use. Drawn separately from `oddityKinds` because a hoard
   * has to tile a room: it must be solid, roughly square in plan, and read as
   * something there could plausibly be ninety of.
   */
  hoardKinds: readonly PropKind[];
}

/** A weighted entry in one of the draw tables above. */
export interface PropWeight {
  kind: PropKind;
  weight: number;
}

const BASE_DENSITY = {
  lobby: {
    cellsPerCluster: 178,
    clusterMax: 4,
    spacing: 6,
    ceilingPer1k: 1.0,
    ceilingKinds: ['ceiling-stain', 'hanging-light'],
    landmarks: {
      graffiti: [3, 5],
      'tally-marks': [1, 3],
      arrow: [2, 4],
      sign: [1, 3],
      camp: [1, 2],
    },
    architecturePer1k: 3.2,
    architectureKinds: [
      { kind: 'half-wall', weight: 20 },
      { kind: 'stub-wall', weight: 15 },
      { kind: 'free-doorframe', weight: 13 },
      { kind: 'false-door', weight: 12 },
      { kind: 'partition', weight: 11 },
      { kind: 'soffit', weight: 10 },
      { kind: 'inner-window', weight: 9 },
      { kind: 'column-cluster', weight: 8 },
      { kind: 'stairs-nowhere', weight: 8 },
    ],
    oddityChance: 0.12,
    hoardChance: 0.02,
    // Office and domestic, in the same room, without comment. The heavy weights
    // are the things a fit-out contractor could have left; the light ones are
    // the things nobody could have.
    oddityKinds: [
      { kind: 'desk', weight: 12 },
      { kind: 'chair-pile', weight: 8 },
      { kind: 'filing-tower', weight: 8 },
      { kind: 'bookshelf', weight: 8 },
      { kind: 'sofa', weight: 7 },
      { kind: 'dead-plant', weight: 7 },
      { kind: 'mannequin', weight: 6 },
      { kind: 'bathtub', weight: 6 },
      { kind: 'mattress', weight: 6 },
      { kind: 'tv-stack', weight: 6 },
      { kind: 'standing-lamp', weight: 6 },
      { kind: 'mirror', weight: 6 },
      { kind: 'phone-booth', weight: 5 },
      { kind: 'sink', weight: 5 },
      { kind: 'wall-clock', weight: 5 },
      { kind: 'carpet-roll', weight: 5 },
      { kind: 'door-pile', weight: 5 },
      { kind: 'bed-frame', weight: 4 },
      { kind: 'shopping-cart', weight: 4 },
      { kind: 'bicycle', weight: 4 },
      { kind: 'piano', weight: 3 },
      { kind: 'fish-tank', weight: 3 },
      { kind: 'chandelier', weight: 2 },
      // The one object here that is a person rather than their furniture. Kept
      // to about one level in three, because a shoe you find twice is litter.
      { kind: 'shoe', weight: 4 },
    ],
    hoardKinds: ['chair-pile', 'desk', 'filing-tower', 'door-pile', 'bookshelf', 'tv-stack'],
  },
  warehouse: {
    // Racking bays are enormous and mostly wall-free, so a per-cell rate that
    // suits the Lobby would leave the whole level bare. Counted against
    // walkable area it still comes out sparser than Level 0 per square metre.
    cellsPerCluster: 215,
    clusterMax: 4,
    spacing: 7,
    ceilingPer1k: 0.9,
    ceilingKinds: ['ceiling-stain', 'hanging-light'],
    landmarks: {
      graffiti: [2, 4],
      'tally-marks': [1, 2],
      arrow: [2, 3],
      sign: [2, 3],
      camp: [1, 2],
    },
    // Less than the Lobby per square metre: a bay is forty metres of nothing
    // and the nothing is what it is for. What does go in is heavy and blank —
    // column clusters and walls that stop, which read as structure gone wrong
    // rather than as furniture.
    architecturePer1k: 1.7,
    architectureKinds: [
      { kind: 'stub-wall', weight: 20 },
      { kind: 'column-cluster', weight: 16 },
      { kind: 'half-wall', weight: 14 },
      { kind: 'free-doorframe', weight: 10 },
      { kind: 'stairs-nowhere', weight: 10 },
      { kind: 'partition', weight: 8 },
      { kind: 'soffit', weight: 8 },
      { kind: 'false-door', weight: 8 },
      { kind: 'inner-window', weight: 6 },
    ],
    oddityChance: 0.19,
    hoardChance: 0.02,
    oddityKinds: [
      { kind: 'shopping-cart', weight: 10 },
      { kind: 'door-pile', weight: 9 },
      { kind: 'chair-pile', weight: 8 },
      { kind: 'filing-tower', weight: 8 },
      { kind: 'hydrant', weight: 8 },
      { kind: 'bicycle', weight: 7 },
      { kind: 'carpet-roll', weight: 7 },
      { kind: 'desk', weight: 7 },
      { kind: 'mannequin', weight: 6 },
      { kind: 'tv-stack', weight: 5 },
      { kind: 'phone-booth', weight: 5 },
      { kind: 'mattress', weight: 5 },
      { kind: 'sofa', weight: 4 },
      { kind: 'dead-plant', weight: 4 },
      { kind: 'bathtub', weight: 3 },
      { kind: 'piano', weight: 3 },
      { kind: 'bed-frame', weight: 3 },
      { kind: 'mirror', weight: 3 },
      { kind: 'sink', weight: 3 },
      { kind: 'shoe', weight: 4 },
    ],
    hoardKinds: ['door-pile', 'chair-pile', 'filing-tower', 'shopping-cart', 'bicycle'],
  },
  pipes: {
    // Fittings on the wall of a service corridor are the level's whole visual
    // vocabulary; without them every metre of the maze is literally identical.
    cellsPerCluster: 105,
    clusterMax: 3,
    spacing: 5,
    ceilingPer1k: 0.9,
    ceilingKinds: ['ceiling-stain'],
    landmarks: {
      graffiti: [2, 4],
      'tally-marks': [2, 3],
      arrow: [3, 5],
      sign: [1, 2],
      camp: [1, 1],
    },
    // A one-cell maze has almost no floor a free-standing thing can occupy, so
    // nearly all of this lands on a wall: doors that were never doors, windows
    // into the rock. Which is the right answer down here anyway.
    architecturePer1k: 2.2,
    architectureKinds: [
      { kind: 'false-door', weight: 26 },
      { kind: 'inner-window', weight: 20 },
      { kind: 'stub-wall', weight: 14 },
      { kind: 'free-doorframe', weight: 14 },
      { kind: 'soffit', weight: 12 },
      { kind: 'stairs-nowhere', weight: 8 },
      { kind: 'half-wall', weight: 6 },
    ],
    oddityChance: 0.2,
    hoardChance: 0.008,
    // Service-duct clutter: plumbing and white goods, in a corridor two and a
    // half metres high. Nothing here was carried in through a door.
    oddityKinds: [
      { kind: 'hydrant', weight: 12 },
      { kind: 'sink', weight: 10 },
      { kind: 'mattress', weight: 9 },
      { kind: 'tv-stack', weight: 8 },
      { kind: 'standing-lamp', weight: 7 },
      { kind: 'mirror', weight: 7 },
      { kind: 'bicycle', weight: 6 },
      { kind: 'door-pile', weight: 6 },
      { kind: 'dead-plant', weight: 5 },
      { kind: 'shopping-cart', weight: 5 },
      { kind: 'desk', weight: 4 },
      { kind: 'phone-booth', weight: 4 },
      { kind: 'bathtub', weight: 3 },
      { kind: 'fish-tank', weight: 3 },
      { kind: 'shoe', weight: 4 },
    ],
    hoardKinds: ['sink', 'hydrant', 'tv-stack', 'door-pile'],
  },
  poolrooms: {
    cellsPerCluster: 260,
    clusterMax: 3,
    spacing: 7,
    ceilingPer1k: 0.9,
    ceilingKinds: ['ceiling-stain'],
    // The one level nothing has happened on. Fewer traces of anyone.
    landmarks: {
      graffiti: [1, 2],
      'tally-marks': [0, 1],
      arrow: [1, 2],
      sign: [1, 2],
      camp: [0, 1],
    },
    // Almost nothing, and that is the level. What little there is has to be
    // worth the walk across an empty tiled hall to look at: one armchair
    // standing in the shallow end beats twenty of anything.
    architecturePer1k: 0.4,
    architectureKinds: [
      { kind: 'free-doorframe', weight: 26 },
      { kind: 'column-cluster', weight: 22 },
      { kind: 'half-wall', weight: 18 },
      { kind: 'stub-wall', weight: 14 },
      { kind: 'false-door', weight: 12 },
      { kind: 'inner-window', weight: 8 },
    ],
    oddityChance: 0.04,
    // No hoards at all. A tiled hall with twenty of anything in it is a room
    // with twenty things in it; the level only works if what you find is one
    // armchair, once, standing in the shallow end.
    hoardChance: 0,
    oddityKinds: [
      { kind: 'sofa', weight: 24 },
      { kind: 'mirror', weight: 14 },
      { kind: 'standing-lamp', weight: 12 },
      { kind: 'mannequin', weight: 10 },
      { kind: 'dead-plant', weight: 8 },
      { kind: 'phone-booth', weight: 6 },
      { kind: 'chandelier', weight: 6 },
      { kind: 'desk', weight: 5 },
    ],
    hoardKinds: [],
  },
  hub: {
    cellsPerCluster: 46,
    clusterMax: 4,
    spacing: 3,
    ceilingPer1k: 1.0,
    ceilingKinds: ['ceiling-stain'],
    landmarks: {
      graffiti: [1, 2],
      'tally-marks': [1, 1],
      arrow: [0, 1],
      sign: [1, 1],
      camp: [1, 1],
    },
    // The Alcove is the one place the building still makes sense. Nothing
    // structural goes wrong here, because the levels either side of it are
    // measured against how ordinary this one is. Somebody did stack the chairs.
    architecturePer1k: 0,
    architectureKinds: [],
    oddityChance: 0.1,
    hoardChance: 0.08,
    oddityKinds: [
      { kind: 'chair-pile', weight: 20 },
      { kind: 'desk', weight: 14 },
      { kind: 'bookshelf', weight: 12 },
      { kind: 'standing-lamp', weight: 10 },
      { kind: 'dead-plant', weight: 10 },
      { kind: 'sofa', weight: 8 },
      { kind: 'wall-clock', weight: 8 },
      { kind: 'mirror', weight: 6 },
    ],
    hoardKinds: ['chair-pile'],
  },
  funrooms: {
    /*
     * This table no longer fills the level, and that is the change.
     *
     * It used to be the only thing that did — at 108 cells per pocket it was
     * already the densest profile in the game, and it still came out as five
     * climbing frames and two bouncy castles across forty thousand square
     * metres of blue carpet, because a pocket of scenery placed against a wall
     * is simply not the unit a soft-play hall is built out of. The bays are now
     * fitted out by `dressFunrooms` in `world/gen/funrooms.ts`, which lays the
     * frame, the ball pool and the wall lining as a plan rather than drawing
     * them one at a time.
     *
     * So this pass has gone back to being what it is everywhere else: texture,
     * in the parts of the level the fit-out does not reach — the tubes, the
     * crawls, the walkway galleries and the pockets at the end of a dead end.
     * 108 to 128 is the whole of that retreat, and it is a small one, because
     * the two passes do not compete: the fit-out claims the ground it uses
     * before this one is consulted, and every one of these pockets lands in the
     * gaps left over. What went *up* instead is the ceiling budget — a hall
     * with a climbing frame filling it wants paper chains over the top of it —
     * and the oddities, which are the only unsettling things on the level and
     * now have to hold their own against a great deal more cheerfulness.
     */
    cellsPerCluster: 128,
    clusterMax: 4,
    spacing: 5,
    ceilingPer1k: 3.4,
    // Paper chains between the ceiling points are half of what makes a hall read
    // as decorated rather than as empty, so they are the first thing the ceiling
    // pass reaches for here and the budget is doubled to carry them.
    ceilingKinds: ['streamers', 'ceiling-stain', 'hanging-light'],
    landmarks: {
      graffiti: [3, 5],
      'tally-marks': [1, 3],
      arrow: [2, 4],
      sign: [1, 3],
      camp: [1, 2],
    },
    architecturePer1k: 3.2,
    architectureKinds: [
      { kind: 'half-wall', weight: 20 },
      { kind: 'stub-wall', weight: 15 },
      { kind: 'free-doorframe', weight: 13 },
      { kind: 'false-door', weight: 12 },
      { kind: 'partition', weight: 11 },
      { kind: 'soffit', weight: 10 },
      { kind: 'inner-window', weight: 9 },
      { kind: 'column-cluster', weight: 8 },
      { kind: 'stairs-nowhere', weight: 8 },
    ],
    oddityChance: 0.16,
    hoardChance: 0.03,
    // Entertainment, at the settings somebody who ran a children's venue would
    // have picked. The heavy weights are the fittings of the room it is trying
    // to be — seating, machines, a piano, mirrors, lights over the floor. The
    // light ones are the domestic things that have no business at a party, and
    // they are what stops it being cheerful. Nothing here is office kit: this
    // level is the one place in the building that was decorated on purpose.
    oddityKinds: [
      { kind: 'ball-pit', weight: 13 },
      { kind: 'play-slide', weight: 12 },
      { kind: 'bouncy-castle', weight: 9 },
      { kind: 'crawl-tube', weight: 9 },
      { kind: 'arcade-cabinet', weight: 9 },
      { kind: 'party-table', weight: 9 },
      // The things that have no business at a party, and are what stop it
      // being cheerful. Kept light: one mannequin in a playroom is the whole
      // effect, and three is a shop.
      { kind: 'mannequin', weight: 6 },
      { kind: 'mirror', weight: 5 },
      { kind: 'piano', weight: 5 },
      { kind: 'chair-pile', weight: 5 },
      { kind: 'tv-stack', weight: 4 },
      { kind: 'shoe', weight: 4 },
    ],
    // A crammed room here is the store cupboard of a children's venue, not an
    // office clear-out: the seating and the machines went in first and the rest
    // was piled in after.
    hoardKinds: ['soft-blocks', 'arcade-cabinet', 'party-table', 'chair-pile'],
  },
  motion: {
    cellsPerCluster: 178,
    clusterMax: 4,
    spacing: 6,
    ceilingPer1k: 1.0,
    ceilingKinds: ['ceiling-stain', 'hanging-light'],
    landmarks: {
      graffiti: [3, 5],
      'tally-marks': [1, 3],
      arrow: [2, 4],
      sign: [1, 3],
      camp: [1, 2],
    },
    // Half the Lobby's rate, and only the kinds that are still a joke outdoors.
    // The rest of that list is *pieces of a room* — a partition, a soffit, an
    // internal window, a length of half-wall — and on a lawn the renderer draws
    // each of them as a blank white slab standing in the grass with the house
    // texture on it. That does not read as the building being wrong; it reads as
    // the renderer being wrong, which is the one failure this level cannot
    // afford, because everything else about it is deliberately almost-right.
    //
    // What is left works outside: a doorframe standing on a lawn with no house
    // round it, a front door in a wall that opens on nothing, three steps up to
    // the sky, a row of columns holding up air.
    architecturePer1k: 1.6,
    architectureKinds: [
      { kind: 'free-doorframe', weight: 30 },
      { kind: 'false-door', weight: 26 },
      { kind: 'stairs-nowhere', weight: 24 },
      { kind: 'column-cluster', weight: 20 },
    ],
    oddityChance: 0.12,
    hoardChance: 0.02,
    // A street, furnished by somebody who had only ever had one described to
    // them. The heavy weights are the things that belong outside a house — a
    // bike, a hydrant, a bench, the bulky waste somebody put at the kerb. The
    // light ones are indoor furniture standing on a lawn, which is the joke the
    // whole level is: everything is nearly right.
    //
    // `chandelier` is a ceiling mount and the lanes are `Cell.Open`, so the
    // placer can only hang one inside a house. That is the correct outcome and
    // is why it is still on the list.
    oddityKinds: [
      { kind: 'garden-tree', weight: 16 },
      { kind: 'parked-car', weight: 13 },
      { kind: 'swing-set', weight: 12 },
      { kind: 'lamp-post', weight: 11 },
      { kind: 'laundry-line', weight: 10 },
      { kind: 'mailbox', weight: 9 },
      { kind: 'bicycle', weight: 8 },
      { kind: 'hydrant', weight: 7 },
      // A suburb with a bathtub on the lawn is still a suburb, and is worse
      // for it. Rare on purpose.
      { kind: 'bathtub', weight: 5 },
      { kind: 'sofa', weight: 5 },
      { kind: 'mannequin', weight: 4 },
    ],
    hoardKinds: ['mattress', 'bed-frame', 'tv-stack', 'carpet-roll', 'door-pile'],
  },
} satisfies Record<string, DensityProfile>;

export const DENSITY: Record<GeneratorKind, DensityProfile> = {
  ...BASE_DENSITY,

  /*
   * Level 94 is deliberately NOT thinned here, and the reason is worth writing
   * down because the obvious fix does not work.
   *
   * It carries about 1660 props against a next-highest level of 630, and the
   * instinct is to raise `cellsPerCluster` on this row. Measured: 178 gives 1663
   * props and 232 gives 1629. Thirty-four. Almost nothing on this level comes
   * through `placeClusters` at all — 1254 of those props are fences, shrubs,
   * trees, hills, roofs and window bays placed by `dressMotion`, which fits them
   * to the plan and never consults this table. Turning this knob only thins the
   * kerbside clutter, which is the part of the level that is not the problem.
   *
   * The lever that does work is `LevelSpec.viewDistance`, which is 118 here.
   */

  /*
   * An office floor plate. Denser than the Lobby because it is furniture rather
   * than debris and a cube farm is *supposed* to be full, but with the
   * architectural nonsense turned down: this level's wrongness is that it is a
   * completely ordinary office, and a wall that stops in mid-air argues with
   * that. The oddities lean hard on the office end of the Lobby's table.
   */
  offices: {
    ...BASE_DENSITY.lobby,
    cellsPerCluster: 150,
    architecturePer1k: 1.8,
    oddityChance: 0.14,
    oddityKinds: [
      { kind: 'desk', weight: 20 },
      { kind: 'filing-tower', weight: 16 },
      { kind: 'chair-pile', weight: 14 },
      { kind: 'bookshelf', weight: 10 },
      { kind: 'partition', weight: 8 },
      { kind: 'dead-plant', weight: 8 },
      { kind: 'sofa', weight: 6 },
      { kind: 'tv-stack', weight: 5 },
      { kind: 'wall-clock', weight: 5 },
      { kind: 'mirror', weight: 4 },
      { kind: 'mannequin', weight: 2 },
      { kind: 'bathtub', weight: 2 },
      { kind: 'shoe', weight: 5 },
    ],
    hoardKinds: ['desk', 'chair-pile', 'filing-tower', 'bookshelf'],
  },

  /*
   * Switchgear. The pipe level's profile with more of everything, because Level
   * 3 is the most heavily serviced floor in the game and the one the M.E.G.
   * actually used — the wiki puts about seventy per cent of its supplies here,
   * which means people, which means marks on the walls.
   */
  station: {
    ...BASE_DENSITY.pipes,
    cellsPerCluster: 96,
    landmarks: {
      graffiti: [2, 4],
      'tally-marks': [2, 4],
      arrow: [3, 5],
      sign: [2, 4],
      camp: [1, 2],
    },
  },

  /*
   * Three hundred guest rooms, mostly clean. Clutter is sparse and the oddities
   * are domestic — this is the one level where a bed frame and a bathtub are
   * not out of place at all, which is why the *out*-of-place things here are
   * the office furniture instead.
   */
  hotel: {
    ...BASE_DENSITY.lobby,
    cellsPerCluster: 205,
    architecturePer1k: 2.4,
    oddityChance: 0.16,
    oddityKinds: [
      { kind: 'bed-frame', weight: 16 },
      { kind: 'mattress', weight: 14 },
      { kind: 'sofa', weight: 12 },
      { kind: 'standing-lamp', weight: 10 },
      { kind: 'mirror', weight: 9 },
      { kind: 'bathtub', weight: 8 },
      { kind: 'sink', weight: 8 },
      { kind: 'dead-plant', weight: 7 },
      { kind: 'tv-stack', weight: 6 },
      { kind: 'piano', weight: 4 },
      { kind: 'chandelier', weight: 3 },
      { kind: 'desk', weight: 3 },
      { kind: 'shoe', weight: 5 },
    ],
    hoardKinds: ['bed-frame', 'mattress', 'chair-pile', 'door-pile'],
  },

  /*
   * Bare. Every number here is zero on purpose and this is the whole level: the
   * wiki gives Level 6 no entities, no supplies and nothing to look at, and the
   * one thing the game must not do is fill it in. `THEMES.lightsout` is empty
   * so `placeClusters` does nothing; `architecturePer1k` and both oddity rolls
   * are zero so no pass has anything to draw.
   *
   * The landmarks are the exception and they are the level. A tally block and
   * an arrow in a corridor with eleven working lights on it are the only
   * evidence anybody has been here, and in the dark they are the only thing to
   * find. `cellsPerCluster` is left absurdly high rather than removed so that a
   * future theme table cannot accidentally flood the level.
   */
  lightsout: {
    ...BASE_DENSITY.warehouse,
    cellsPerCluster: 100000,
    clusterMax: 1,
    ceilingPer1k: 0,
    ceilingKinds: [],
    landmarks: {
      graffiti: [2, 3],
      'tally-marks': [1, 3],
      arrow: [2, 4],
      sign: [0, 1],
      camp: [1, 1],
    },
    architecturePer1k: 0,
    architectureKinds: [],
    oddityChance: 0,
    hoardChance: 0,
    oddityKinds: [],
    hoardKinds: [],
  },

  /*
   * Outdoors, and the streets are wide, so a cluster budget tuned for rooms
   * puts a traffic cone every few metres of open asphalt. Thinner than any
   * indoor level, with no ceiling pass at all — there is no ceiling — and no
   * structural nonsense, because Level 11's wrongness is at the scale of the
   * city rather than of a room: buildings pushed into the road, streets that
   * seal, and the fact that none of it ends.
   */
  city: {
    ...BASE_DENSITY.warehouse,
    cellsPerCluster: 290,
    clusterMax: 3,
    spacing: 8,
    ceilingPer1k: 0,
    ceilingKinds: [],
    landmarks: {
      graffiti: [3, 5],
      'tally-marks': [1, 2],
      arrow: [2, 4],
      sign: [2, 4],
      camp: [1, 2],
    },
    architecturePer1k: 0,
    architectureKinds: [],
    oddityChance: 0.09,
    hoardChance: 0.01,
    oddityKinds: [
      { kind: 'shopping-cart', weight: 14 },
      { kind: 'bicycle', weight: 13 },
      { kind: 'hydrant', weight: 12 },
      { kind: 'phone-booth', weight: 11 },
      { kind: 'mattress', weight: 9 },
      { kind: 'sofa', weight: 8 },
      { kind: 'tv-stack', weight: 7 },
      { kind: 'dead-plant', weight: 7 },
      { kind: 'door-pile', weight: 6 },
      { kind: 'carpet-roll', weight: 6 },
      { kind: 'mannequin', weight: 4 },
      { kind: 'piano', weight: 3 },
      { kind: 'shoe', weight: 5 },
    ],
    hoardKinds: ['shopping-cart', 'bicycle', 'door-pile', 'carpet-roll'],
  },
};
